import React, { useRef, useState, useEffect } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import i18next from 'i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { plusIcon, minusIcon } from '@progress/kendo-svg-icons';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import Checkbox from '@material-ui/core/Checkbox';
import { SvgIcon } from '@progress/kendo-react-common';
import AlertAlgo from '@controls/AlertSnack';
import { getInfoPunchAndReport } from '@actions/reportDaily';
import * as StatusCode from '@scripts/StatusCode';
import { IsEnglish } from '@utils/language';
import { formatDate, getDescriptionWeekDay } from '@utils';
import { saveDailyReport } from '@actions/reportDaily/index';
import { TimePicker } from '@progress/kendo-react-dateinputs';
import { IntlProvider, LocalizationProvider } from "@progress/kendo-react-intl";
import { loadLocalization } from '@utils/kendoLocalization';
import moment from "moment";
import { GetLanguage } from '@utils/language';
import { Popup } from "@progress/kendo-react-popup";

loadLocalization();
moment.locale("fr");

const fetchReport = (punchId, reportId) => {
    return new Promise((resolve) => {
        resolve(getInfoPunchAndReport(punchId, reportId));
    });
};

const useStyles = makeStyles((theme) => ({
    textField: {
        '& textarea': {
            height: '1em !important'
        }
    },
    title: {
        marginLeft: theme.spacing(2),
        flex: 1,
    },
}));

const StyledFirstTableCell = withStyles((theme) => ({
    body: {
        fontSize: 14,
        width: '200px',
        textAlign: 'right !important',
        '@media (max-width: 600px)': {
            width: "150px"
        },
        fontWeight: "bold"
    },
}))(TableCell);

const hasInvalidTimeRange = (item) => {
    return item.startTime !== '' && item.endTime !== '' && item.endTime <= item.startTime;
}

// Height (px) of the app's bottom navigation bar (Accueil / Documents / Poinçons...).
const BOTTOM_NAV_HEIGHT = 57;
// Approximate height (px) reserved at the end of the form so the last
// rows are not hidden behind the fixed save bar.
const SAVE_BAR_HEIGHT = 64;

const createEmptyIncident = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, // unique key
    reportIncidentId: 0,
    incident: '',
    startTimeString: '',
    endTimeString: '',
    action: ''
});

export default function ReportDaily(props) {
    const classes = useStyles();

    const { punchId, reportDailyId, onClose, isEnabled } = props;

    // Read-only mode: when isEnabled is false, every input, checkbox and button is disabled
    const isReadOnly = !isEnabled;

    const alertRef = useRef(null);
    const popupContainerRef = useRef(null);
    const [locale, setLocale] = useState(GetLanguage());
    const [listEquipments, setListEquipments] = useState([]);
    const [listeTypeIncident, setListeTypeIncident] = useState([]);
    const [checkedEquipments, setCheckedEquipments] = useState([]);
    const [checkedTypeIncidents, setCheckedTypeIncidents] = useState([]);
    const [equipmentValues, setEquipmentValues] = useState({});
    const [reportInfo, setReportInfo] = useState({
        date: '',
        day: '',
        contract: '',
        shift: '',
        startTime: '',
        endTime: '',
        address: '',
        agent: '',
        reliefAgent: '',
        descriptionTask: ''
    });

    // --- Loading / edit mode ---
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [reportId, setReportId] = useState(null); // null = new report, otherwise editing

    useEffect(() => {
        if (!isLoading) {
            resizePage();
        }
    }, [isLoading]);

    // --- Load existing report from the "database" on mount / when punchId changes ---
    useEffect(() => {
        let cancelled = false;

        const loadReport = async () => {
            setIsLoading(true);
            try {
                const data = await fetchReport(punchId, reportDailyId);

                if (data.status !== StatusCode.STATUS_OK) {
                    cancelled = true;
                }

                if (cancelled) {
                    return;
                }

                if (data.result != null) {
                    const report = data.result.report;

                    setReportId(report.reportId);
                    setReportInfo({
                        date: formatDate(report.dateProcessingCalculation, 'date') ?? '',
                        day: getDescriptionWeekDay(report.startDate) ?? '',
                        contract: (IsEnglish() ? report.projectEnglish : report.projectFrench) ?? '',
                        shift: (IsEnglish() ? report.shiftEnglish : report.shiftFrench) ?? '',
                        startTimeString: formatDate(report.startDate) ?? '',
                        endTimeString: formatDate(report.endDate) ?? '',
                        address: report.siteAddress ?? '',
                        agent: report.agent ?? '',
                        reliefAgent: report.reliefAgent ?? '',
                        descriptionTask: report.descriptionTask ?? ''
                    });
                    setListEquipments(data.result.equipmentList);
                    setListeTypeIncident(data.result.incidentTypeList);
                    setCheckedEquipments(
                        (data.result.equipmentReport ?? []).filter(x => x.type == 'bool')
                    );
                    setCheckedTypeIncidents(data.result.incidentTypeReport ?? []);
                    setEquipmentValues(
                        (data.result.equipmentReport ?? [])
                            .filter(x => x.type == 'decimal')
                            .reduce((acc, item) => {
                                acc[item.equipmentId] = item.customValue != null ? String(item.customValue) : '';
                                return acc;
                            }, {})
                    );
                    setIncidents(
                        data.result.incidentReport != null && data.result.incidentReport.length > 0
                            ? data.result.incidentReport.map(inc => ({
                                id: `db-${inc.reportIncidentId}`,
                                reportIncidentId: inc.reportIncidentId,
                                incident: inc.descriptionIncident ?? '',
                                startTimeString: inc.startTime ?? '',
                                endTimeString: inc.endTime ?? '',
                                action: inc.descriptionAction ?? ''
                            }))
                            : [createEmptyIncident()]
                    );
                }
                // report == null => keep the empty defaults (new report mode)
            } catch (error) {
                console.error(error);
                showMessageError(i18next.t('error:generic-error'));
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadReport();

        // Ignore the result if the component unmounts (or punchId changes) mid-fetch
        return () => { cancelled = true; };
    }, [punchId]);

    const resizePage = () => {
        setTimeout(() => {
            const header = document.querySelector('header')?.clientHeight ?? 0;
            const element = document.querySelector('#divForm');
            const offSetHeight = 15;
            if (element != null) {
                element.style.height = `calc(100vh - ${header + BOTTOM_NAV_HEIGHT - offSetHeight}px)`;
                element.style.overflowY = 'auto';
                element.style.overflowX = 'hidden';
            }
            // Prevent the page itself from scrolling — only #divForm scrolls
            document.body.style.overflow = 'hidden';
        }, 100);
    };



    const handleReportInfoChange = (field, value) => {
        setReportInfo(prev => ({ ...prev, [field]: value }));
    };

    const [incidents, setIncidents] = useState([createEmptyIncident()]);

    const handleAddIncident = () => {
        setIncidents(prev => [...prev, createEmptyIncident()]);
    };

    const handleRemoveIncident = (id) => {
        setIncidents(prev => prev.filter(item => item.id !== id));
    };

    const handleIncidentChange = (id, field, value) => {
        setIncidents(prev =>
            prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    const handleIncidentTimeChange = (id, field, value) => {
        const fullValue = value === '' ? '' : `${reportInfo.date}T${value}`;
        handleIncidentChange(id, field, fullValue);
    };

    const toTimeDate = (value) => {
        if (value == null || value === '') return null;
        const d = moment(value).toDate();
        return isNaN(d.getTime()) ? null : d;
    };

    const isIncidentEmpty = (item) =>
        (item.incident ?? '').trim() === '' &&
        (item.startTimeString ?? '') === '' &&
        (item.endTimeString ?? '') === '' &&
        (item.action ?? '').trim() === '';

    const hasReportData = () =>
        reportInfo.reliefAgent.trim() !== '' ||
        reportInfo.descriptionTask.trim() !== '' ||
        checkedEquipments.length > 0 ||
        Object.values(equipmentValues).some(v => v !== '' && v !== '.' && v !== ',') ||
        checkedTypeIncidents.length > 0 ||
        incidents.some(item => !isIncidentEmpty(item));

    const handleSave = async () => {
        if (isSaving || isReadOnly) {
            return;
        }

        if (!hasReportData()) {
            showMessageError(i18next.t('dailyReport:validation.emptyReport'));
            return;
        }

        const invalidIndexes = incidents
            .map((item, index) => (hasInvalidTimeRange(item) ? index + 1 : null))
            .filter(Boolean);

        if (invalidIndexes.length > 0) {
            showMessageError(
                i18next.t('dailyReport:validation.endTimeBeforeStartTime', { list: invalidIndexes.join(', ') })
            );
            return;
        }

        const payload = {
            reportId,
            punchId,
            agent: reportInfo.agent,
            reliefAgent: reportInfo.reliefAgent,
            descriptionTask: reportInfo.descriptionTask,
            equipments: checkedEquipments,
            equipmentValues: Object.entries(equipmentValues)
                .filter(([, value]) => value !== '' && value !== '.' && value !== ',')
                .map(([id, value]) => ({
                    id: Number(id),
                    value: value
                })),
            typeIncidents: checkedTypeIncidents,
            incidents: incidents.map(({ id, ...rest }) => rest)
        };

        //console.log('Saving report:', JSON.stringify(payload));

        setIsSaving(true);

        try {
            const result = await saveDailyReport(payload);

            if (result.status === StatusCode.STATUS_OK) {
                //showMessageSuccess(i18next.t('common:successMessage'));
                onClose(true);
            } else {
                showMessageError(i18next.t('error:generic-error'));
            }
        } catch (error) {
            //console.error(error);
            showMessageError(i18next.t('error:generic-error'));
        } finally {
            // Re-enable the button (matters when the save failed and the form stays open)
            setIsSaving(false);
        }
    };

    const showMessageSuccess = (message) => {
        alertRef.current.showMessageSuccess(message);
    }

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const handleEquipmentToggle = (id) => {
        setCheckedEquipments(prev =>
            prev.some(x => x.equipmentId === id)
                ? prev.filter(x => x.equipmentId !== id)
                : [...prev, { equipmentId: id, reportEquipmentId: 0, type: 'bool' }]
        );
    };

    const handleEquipmentValueChange = (id, value) => {
        setEquipmentValues(prev => ({ ...prev, [id]: value }));
    };

    const handleTypeIncidentToggle = (id) => {
        setCheckedTypeIncidents(prev =>
            prev.some(x => x.incidentTypeId === id)
                ? prev.filter(x => x.incidentTypeId !== id)
                : [...prev, { incidentTypeId: id, reportIncidentTypeId: 0 }]
        );
    };

    const equipmentItems = (id, text, type) => {
        return (
            <TableRow key={id}>
                <StyledFirstTableCell>{text}</StyledFirstTableCell>
                <TableCell>
                    {type === 'decimal' ? (
                        <TextField
                            value={equipmentValues[id] ?? ''}
                            className={classes.textField}
                            disabled={isReadOnly}
                            inputProps={{ style: { maxWidth: '120px' } }}
                            onChange={(e) => handleEquipmentValueChange(id, e.target.value)}
                        />
                    ) : (
                        <Checkbox
                            checked={checkedEquipments.some(x => x.equipmentId === id)}
                            disabled={isReadOnly}
                            onChange={() => handleEquipmentToggle(id)}
                            name="equipment"
                        />
                    )}
                </TableCell>
            </TableRow>
        )
    }

    const typeIncidentItems = (id, text) => {
        return (
            <TableRow key={id}>
                <StyledFirstTableCell>{text}</StyledFirstTableCell>
                <TableCell>
                    <Checkbox
                        checked={checkedTypeIncidents.some(x => x.incidentTypeId === id)}
                        disabled={isReadOnly}
                        onChange={() => handleTypeIncidentToggle(id)}
                        name="typeIncident"
                    />
                </TableCell>
            </TableRow>
        )
    }

    const incidentItem = (item, index) => {
        return (
            <React.Fragment key={item.id}>
                <TableRow>
                    <StyledFirstTableCell>
                        {i18next.t('dailyReport:form.incident-label')} #{index + 1}
                    </StyledFirstTableCell>
                    <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                value={item.incident}
                                className={classes.textField}
                                disabled={isReadOnly}
                                onChange={(e) => handleIncidentChange(item.id, 'incident', e.target.value)}
                            />
                            <IconButton
                                size="small"
                                title="Supprimer cet incident"
                                disabled={isReadOnly}
                                onClick={() => handleRemoveIncident(item.id)}
                            >
                                <SvgIcon icon={minusIcon} />
                            </IconButton>
                        </div>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <StyledFirstTableCell>{i18next.t('dailyReport:form.incident-start-end-time-label')}</StyledFirstTableCell>
                    <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <TimePicker
                                value={toTimeDate(item.startTimeString)}
                                disabled={isReadOnly}
                                width="110px"
                                onChange={(e) =>
                                    handleIncidentTimeChange(
                                        item.id,
                                        'startTimeString',
                                        e.value != null ? moment(e.value).format('HH:mm') : ''
                                    )
                                }
                                popup={props => <Popup {...props} appendTo={popupContainerRef.current} />}
                            />
                            <span>-</span>
                            <TimePicker
                                value={toTimeDate(item.endTimeString)}
                                disabled={isReadOnly}
                                width="110px"
                                onChange={(e) =>
                                    handleIncidentTimeChange(
                                        item.id,
                                        'endTimeString',
                                        e.value != null ? moment(e.value).format('HH:mm') : ''
                                    )
                                }
                                popup={props => <Popup {...props} appendTo={popupContainerRef.current} />}
                            />
                        </div>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <StyledFirstTableCell>{i18next.t('dailyReport:form.actions-label')}</StyledFirstTableCell>
                    <TableCell>
                        <TextField
                            fullWidth
                            multiline
                            value={item.action}
                            className={classes.textField}
                            disabled={isReadOnly}
                            onChange={(e) => handleIncidentChange(item.id, 'action', e.target.value)}
                        />
                    </TableCell>
                </TableRow>
            </React.Fragment>
        )
    }

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <CircularProgress style={{ color: 'var(--color1)' }} />
            </div>
        );
    }

    return (
        <>
            <div id="divForm" style={{ paddingBottom: `${SAVE_BAR_HEIGHT + 16}px` }}>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        {/* Popup container: inside the dialog, outside the PanelBar */}
                        <div ref={popupContainerRef} style={{ position: 'relative', zIndex: 11000 }} />
                        <PanelBar>
                            <PanelBarItem expanded={true} title={i18next.t('dailyReport:section.section1')}>
                                <div>
                                    <TableContainer component={Paper}>
                                        <Table className={classes.table} aria-label="spanning table">
                                            <TableBody>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.date')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.date}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.day')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.day}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.contract')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.contract}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.shift')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.shift}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.startTime')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.startTimeString}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.endTime')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.endTimeString}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.agent')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.agent}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.relief-agent')}</StyledFirstTableCell>
                                                    <TableCell>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            className={classes.textField}
                                                            value={reportInfo.reliefAgent}
                                                            disabled={isReadOnly}
                                                            onChange={(e) => handleReportInfoChange('reliefAgent', e.target.value)} />
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.address')}</StyledFirstTableCell>
                                                    <TableCell>{reportInfo.siteAddress}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <StyledFirstTableCell>{i18next.t('dailyReport:form.description-task')}</StyledFirstTableCell>
                                                    <TableCell>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            className={classes.textField}
                                                            value={reportInfo.descriptionTask}
                                                            disabled={isReadOnly}
                                                            onChange={(e) => handleReportInfoChange('descriptionTask', e.target.value)} />
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </PanelBarItem>
                            <PanelBarItem title={i18next.t('dailyReport:section.section2')}>
                                <div>
                                    <TableContainer component={Paper}>
                                        <Table className={classes.table} aria-label="spanning table">
                                            <TableBody>
                                                {listEquipments != null && listEquipments.map((obj) =>
                                                    equipmentItems(obj.id, IsEnglish() ? obj.descE : obj.descF, obj.type)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </PanelBarItem>
                            <PanelBarItem title={i18next.t('dailyReport:section.section3')}>
                                <div>
                                    <TableContainer component={Paper}>
                                        <Table className={classes.table} aria-label="spanning table">
                                            <TableBody>
                                                {listeTypeIncident != null && listeTypeIncident.map((obj) =>
                                                    typeIncidentItems(obj.id, IsEnglish() ? obj.descE : obj.descF)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </PanelBarItem>
                            <PanelBarItem title={i18next.t('dailyReport:section.section4')}>
                                <div>
                                    <TableContainer component={Paper}>
                                        <Table className={classes.table} aria-label="spanning table">
                                            <TableBody>
                                                {incidents.map((item, index) => incidentItem(item, index))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
                                        <IconButton
                                            size="small"
                                            title="Ajouter un incident"
                                            disabled={isReadOnly}
                                            onClick={handleAddIncident}
                                        >
                                            <SvgIcon icon={plusIcon} />
                                        </IconButton>
                                    </div>
                                </div>
                            </PanelBarItem>
                        </PanelBar>
                    </IntlProvider>
                </LocalizationProvider>

                {isEnabled && <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '12px 16px',
                        backgroundColor: '#fff',
                        boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    <Button
                        variant="contained"
                        disabled={isSaving}
                        style={
                            isSaving
                                ? {}
                                : { backgroundColor: 'var(--color1)', color: 'var(--color2)' }
                        }
                        startIcon={
                            isSaving
                                ? <CircularProgress size={18} color="inherit" />
                                : null
                        }
                        onClick={handleSave}
                    >
                        {i18next.t('buttons:save')}
                    </Button>
                </div>}
                <AlertAlgo ref={alertRef} />
            </div>
        </>
    )
}